import React from 'react';
import { get, find, cloneDeep, findKey, includes, debounce, fromPairs, map, isEmpty, toInteger, noop, isEqual, remove, size, merge, head, forEach, isNil, isUndefined, filter, findIndex, mapKeys, mapValues, omit, keys } from 'lodash';
import { getMemberSearchSelectionKey } from 'utils/memberDetailSelection';
import moment from 'moment';

// local dependencies
import { getVisibleTableEntities } from 'resolvers/getVisibleEntities';
import { tableSearchFlyoutWidget } from 'ui/views/widgets/tableSearchFlyoutWidget';
import { tableSearchInlineWidget } from 'ui/views/widgets/tableSearchInlineWidget';
import { tableSearchFlyoutWidgetWithAdvancedOptions } from 'ui/views/widgets/tableSearchFlyoutWidgetWithAdvancedOptions';
import { SORT_DIRECTIONS, SIZES } from 'constants/common';
import { TableSearchWidgetService } from 'services/tableSearchWidget';
import { SERVICE_RESPONSE_STATUS, TABLE_TEMPLATE_TYPE, ROW_VIEW_TEMPLATE_TYPE } from 'constants/common';
import * as errorOverlay from 'utils/errorOverlay';
import { sortFilterOffline } from 'resolvers/tableSortFilter';
import { Button } from '@material-ui/core';

/*---------*/

// table view templates
const TABLE_VIEW_TEMPLATES = {
    [ TABLE_TEMPLATE_TYPE.DEFAULT ]: tableSearchFlyoutWidget,
    [ TABLE_TEMPLATE_TYPE.INLINE ]: tableSearchInlineWidget,
    [ TABLE_TEMPLATE_TYPE.ADVANCED_SEARCH ]: tableSearchFlyoutWidgetWithAdvancedOptions
};

// The response flag describes a page of results, not a stable member identity.
// Keep it on each row so a later indexed page cannot inherit an earlier LIVE badge.
let memberSearchRowSequence = 0;
export const markMemberSearchProvenance = ( items, enabled, liveMemberLookup, liveFallback ) => {
    if( ! enabled ) return items;
    return ( items || [] ).map( item => ( {
        ...item,
        __memberSearchRowKey: `member-search-${ ++memberSearchRowSequence }`,
        __liveMemberSearch: true === liveMemberLookup,
        __memberSearchLiveFallback: true === liveFallback,
    } ) );
};

// maps the identity fields collected on the MPID advanced-search panel (keyed by their
// UI/controlled-input names) to the request field names the backend's live-fallback
// identity match expects (`MemberCoverageSearchRequest.identityFirstName/identityLastName/identityDateOfBirth`)
const IDENTITY_SEARCH_REQUEST_FIELD_NAMES = {
    firstName: 'identityFirstName',
    lastName: 'identityLastName',
    dateOfBirth: 'identityDateOfBirth',
};

// the DOB field is populated by `modules/calendar` (see `calendar.view.jsx`), which always
// formats/parses dates as `MM-DD-YYYY` for on-screen display -- that format must NOT change.
const IDENTITY_DATE_OF_BIRTH_DISPLAY_FORMAT = 'MM-DD-YYYY';

// the backend (`MemberCoverageSearchRequest.identityDateOfBirth`) parses the incoming value with
// `java.time.LocalDate.parse(...)`, which requires ISO-8601 (`yyyy-MM-dd`) -- mismatched format
// throws on the backend and leaks the raw DOB into a WARN log. Convert only at the point the
// request is serialized, so the on-screen `MM-DD-YYYY` display is unaffected.
const IDENTITY_DATE_OF_BIRTH_REQUEST_FORMAT = 'YYYY-MM-DD';

// `data.state.initialListSource` value that asks the table to load the live list on mount
const INITIAL_LIST_SOURCE_LIVE = 'live';

const closeExportMenu = ( event ) => {
    const details = event.currentTarget.closest( 'details' );
    const trigger = details.querySelector( 'summary' );
    details.open = false;
    trigger.focus();
};

/*---------*/

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { TableSearchWidget } from 'ui/containers/TableSearchWidget';
 *
 * // render
 * <TableSearchWidget propName='propValue'/>
 */
export class TableSearchWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {

        super( props );

        // create service instance
        this.tableSearchWidgetService = new TableSearchWidgetService();
        this.tableRequestSequence = 0;
        this.liveListRequestInProgress = false;
        this.lastFailedLiveListRequest = null;

        // get visible columns and filters
        const { columns, filters, tabsConfig, selectedTab } = getVisibleTableEntities( cloneDeep( props.tableResponse ) );

        // static component data
        this.data = {
            widgetConfig: get( props, 'tableResponse.structure.widgetConfig', {} ),
            paginationConfig: get( props, 'tableResponse.structure.paginationConfig', {} ),
            filtersConfig: get( props, 'tableResponse.structure.filtersConfig', {} ),
            refreshRowConfig: get( props, 'tableResponse.structure.refreshRowConfig', {} ),
            sortFilterConfig: get( props, 'tableResponse.structure.sortFilterConfig', {} ),
            actionsConfig: get( props, 'tableResponse.structure.actionsConfig', {} ),
            visibility: get( props, 'tableResponse.visibility', {} ),
            values: get( props, 'tableResponse.data.state.values', {} ),
            options: get( props, 'tableResponse.data.options', {} ),
            tabsConfig: tabsConfig,
            mailOrderAccountStatusConfig: {
                mailOrderAccountStatus: get( props, 'tableResponse.data.state.mailOrderAccountStatus', false ),
                ...get( props, 'tableResponse.structure.mailOrderAccountStatusConfig', {} )
            }
        };

        // table data items (rows)
        const items = markMemberSearchProvenance( get( props, 'tableResponse.data.state.items', [] ),
            props.showMemberSearchLiveBadge, get( props, 'tableResponse.data.state.liveMemberLookup', false ),
            get( props, 'tableResponse.data.state.liveFallback', false ) );

        // component state
        this.state = {
            liveFallbackKeys: true === get( props, 'tableResponse.data.state.liveFallback', false ) ? items.map( getMemberSearchSelectionKey ) : [],
            isInitialData: true,
            isFetching: false,
            fetchingType: null,
            networkStatus: null,
            disablePagination: props.disablePagination,
            isLiveMode: false,
            isFetchingLiveList: false,
            liveListAsOf: null,
            liveListError: false, liveListErrorMessage: null, liveListErrorReason: null,

            // table data
            columns,
            filters,
            items,
            sortAndFilter: get( props, 'tableResponse.data.state.sortAndFilter', {} ),
            pagination: get( props, 'tableResponse.data.pagination', {} ),
            totalRecordCount: get( props, 'tableResponse.data.state.totalRecordCount', null ),
            searchableColumns: get( props, 'tableResponse.structure.searchableColumns', {} ),
            callState: get(props, 'callState', {}),

            // selection
            selectedRows: get( props, 'selectedRows', [] ), //[], // get( props, 'selectedRows', [] ),
            selectedTab,
            dynamicSelectedOptions: {},
            customizedRequestData: {},
            // Name & DOB search mode (PBI 107807 fix-wave F): a real entry point for the
            // identity-only (`defaultSearch`) live fallback, independent of the MPID
            // advanced-search toggle above. It deliberately does NOT set
            // `sortAndFilter[name].usingAdvancedSearch` -- the backend only takes the
            // identity-match branch inside `defaultSearch()`, which the request-routing layer
            // (`MemberAppLogicServiceImpl.getMembersSummary`) only calls when
            // `isAdvanceSearch` is `false`. Turning this mode on alongside the MPID toggle
            // would still route to `searchByAdvancefilter()`, whose switch has no case for a
            // name+DOB option, so the two modes are kept mutually exclusive (see
            // `handleSortAndFilter`).
            isIdentitySearchActive: false
        };

        // bind events and methods
        this.__bind();
    }

    /**
     * @desc Bind events and methods to the instance.
     */
    __bind() {
        this.handleFiltersChange = this.handleFiltersChange.bind( this );
        this.handleSortAndFilter = this.handleSortAndFilter.bind( this );
        this.handlePagination = this.handlePagination.bind( this );
        // debounces every `fetchTableItems` call (pagination, sort/filter, plain search, and the
        // MPID advanced-search identity fields alike) by 300ms. Because `customizedRequestData`
        // is updated synchronously via `setState` before this fires, the identity inputs still
        // reflect each keystroke immediately -- only the network request itself is coalesced, so
        // a fast typist filling in first/last name + DOB does not fire one request per keystroke
        // (PBI 107807 -- identity-field debounce).
        this.fetchTableItems = debounce( this.fetchTableItems, 300 ).bind( this );
        this.handleSelection = this.handleSelection.bind( this );
        this.handleTabChange = this.handleTabChange.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
        this.refreshTable = this.refreshTable.bind( this );
        this.handleOnlineSearch = this.handleOnlineSearch.bind( this );
        this.removeRowTable = this.removeRowTable.bind( this );
        this.fetchCurrentList = this.fetchCurrentList.bind( this );
        this.handleCurrentListClick = this.handleCurrentListClick.bind( this );
        this.retryLiveListRequest = this.retryLiveListRequest.bind( this );
    }

    /*****************************************/

     /**
     * @desc Handle search input change
     */
    handleSelection( { item, isChecked } ) {
        log.debug( 'TableSearchWidget.handleSelection()' );

        if( isChecked ) {
            this.setState( {
                selectedRows: [ ...this.state.selectedRows, item ]
            }, () => {

                if( ! isNil( get( this.props, 'onSelectionCallBack', null ) ) ) {
                    this.props.onSelectionCallBack( { values: this.state.selectedRows } );
                }
            } );
        } else {
            const items = [ ...this.state.selectedRows ];

            remove( items, function( id ) {
                return isEqual( id,  item );
            } );

            this.setState( {
                selectedRows: [ ...items ]
            }, () => {
                if( ! isNil( get( this.props, 'onSelectionCallBack', null ) ) ) {
                    this.props.onSelectionCallBack( { values: this.state.selectedRows } );
                }
            } );
        }
    }

    /**
     * @desc Remove the MPID advanced-search identity keys (first/last name, DOB) from a
     * `customizedRequestData` object, leaving any other keys (e.g. `status` from the card
     * filter) untouched. Used to prevent stale identity values from a previous MPID search
     * leaking into an unrelated search once the user is no longer in the identity-search flow.
     */
    clearIdentitySearchFields( customizedRequestData ) {
        return omit( customizedRequestData, keys( IDENTITY_SEARCH_REQUEST_FIELD_NAMES ) );
    }

    /**
     * @desc Handle search input change
     */
    handleFiltersChange( { name, value, customizedRequestData = {} } ) {
        log.debug( 'TableSearchWidget.handleFiltersChange()' );
        // debugger;
        // find filters by `name` and update `value`
        const _filters = cloneDeep( this.state.filters );
        const _filter = find( _filters, { name } );

        if( _filter ) {
            _filter.value = value;

            const hasOfflineSearch = 'offline' === get( this.props, 'searchFilter', undefined );

            // update filters in state and fetch new items
            if( ! hasOfflineSearch && 'search' === name && 0 !== value.length && this.props.minSearchLength > value.length ) {
                // the MPID/search box no longer holds a searchable value: drop any identity fields
                // (first/last name, DOB) already captured, so they can't leak into whatever search
                // eventually runs against this now-different query (PBI 107807 -- stale identity leak)
                return this.setState( { filters: _filters, customizedRequestData: this.clearIdentitySearchFields( this.state.customizedRequestData ) } );  // only update state : if `searchQuery` length is less than `minSearchLength`
            }

            if( ! hasOfflineSearch && 'search' === name && 0 === value.length && ( this.props.templateType === TABLE_TEMPLATE_TYPE.DEFAULT || this.props.templateType === TABLE_TEMPLATE_TYPE.ADVANCED_SEARCH ) ) {
                return this.setState( { filters: _filters, items: [], customizedRequestData: this.clearIdentitySearchFields( this.state.customizedRequestData ) } ); // only update state : if `searchQuery` length is `0` for the `default` template
            } else {
                this.setState( {
                    filters: _filters,
                    isFetching: true,
                    fetchingType: 'filters',
                    pagination: {
                        ...this.state.pagination,
                        startIndex: 0, // reset start index
                    },
                    customizedRequestData: {
                        ...customizedRequestData
                    }
                }, () => {

                    if( 'offline' === get( this.props, 'searchFilter', undefined ) ) {

                        const search = find( this.state.filters, { name: 'search' } );

                        this.setState( {
                            items: sortFilterOffline( {
                                items: this.state.items,
                                filters: this.state.sortAndFilter,
                                columns: this.state.columns,
                                originalItems: get( this.props.tableResponse, 'data.state.items', [] ),
                                searchFilter: search,
                                searchableColumns: get( this.props.tableResponse, 'structure.searchableColumns' )
                            } ),
                            isFetching: false
                        } );

                    } else {

                        this.fetchTableItems( { url: get( this.data, 'filtersConfig.url', {} ) } );

                    }
                } );
            }
        }
    }

    /**
     * @desc Handle sort and filter actions on table columns.
     */
    handleSortAndFilter( { type, name, value, list } ) {
        log.debug( 'TableSearchWidget.handleSortAndFilter()' );

        // clone sort and filter state
        const _sortAndFilter = cloneDeep( this.state.sortAndFilter );

        const searchFilter = find( this.state.filters, { name: 'search' } );

        if( 'memberIdentity' === type ) {
            const customizedRequestData = {
                ...this.state.customizedRequestData,
                [ name ]: value,
            };

            return this.setState( {
                customizedRequestData,
                isFetching: this.props.minSearchLength <= searchFilter.value.length,
                fetchingType: 'sortAndFilter',
                pagination: {
                    ...this.state.pagination,
                    startIndex: 0,
                },
            }, () => {
                if( this.props.minSearchLength <= searchFilter.value.length ) {
                    this.fetchTableItems( { url: get( this.data, 'sortFilterConfig.url', {} ) } );
                }
            } );
        }

        // Name & DOB search mode toggle (PBI 107807 fix-wave F) -- see the `isIdentitySearchActive`
        // state comment in the constructor for why this stays independent of `usingAdvancedSearch`.
        if( 'toggleIdentitySearch' === type ) {
            const isIdentitySearchActive = ! this.state.isIdentitySearchActive;

            return this.setState( {
                isIdentitySearchActive,
                // turning the mode off drops its identity fields, mirroring the MPID panel's
                // existing clearing behavior, so stale identity data can't leak into a later,
                // unrelated search (PBI 107807 -- stale identity leak)
                customizedRequestData: isIdentitySearchActive ? this.state.customizedRequestData : this.clearIdentitySearchFields( this.state.customizedRequestData ),
                isFetching: this.props.minSearchLength <= searchFilter.value.length,
                fetchingType: 'sortAndFilter',
                pagination: {
                    ...this.state.pagination,
                    startIndex: 0, // reset start index
                },
            }, () => {
                // re-fetch on every toggle (on AND off), mirroring the `toggleAdvancedSearch`
                // branch below -- toggling off in particular must re-fetch so the table drops
                // any stale Name & DOB results; without this, they stay on screen until an
                // unrelated action (e.g. pagination or another search) happens to trigger the
                // next fetch
                if( this.props.minSearchLength <= searchFilter.value.length ) {
                    this.fetchTableItems( { url: get( this.data, 'sortFilterConfig.url', {} ) } );
                }
            } );
        }

        if( ! isUndefined( list ) ) {

            const dynamicSelectedOptions = cloneDeep( this.state.dynamicSelectedOptions );
            dynamicSelectedOptions[ name ] = list;
            this.setState( { dynamicSelectedOptions: dynamicSelectedOptions } );
        }

        // advanced search updating filters
        if( 'toggleAdvancedSearch' === type || TABLE_TEMPLATE_TYPE.ADVANCED_SEARCH === type ) {

            // default advanced search column
            let _searchableColumns = _sortAndFilter[ name ].selected;

            if( 'toggleAdvancedSearch' === type ) {
                _sortAndFilter[ name ].usingAdvancedSearch = ! get( this.state, `sortAndFilter[${name}].usingAdvancedSearch`, {} );

                // if advanced search is toggled off,
                // reset searchable columns to that of basic search
                if ( ! _sortAndFilter[ name ].usingAdvancedSearch ) {
                    _searchableColumns = get( this.props, 'tableResponse.structure.searchableColumns', [] );
                }
            } else {
                _sortAndFilter[ name ].selected = [];
                _sortAndFilter[ name ].selected.push( value );

                _searchableColumns = [];
                _searchableColumns.push( value );
            }

            return (
                this.setState( {
                sortAndFilter: _sortAndFilter,
                // toggling advanced search off, or switching the selected advanced-search option
                // away from (or to) `memberId`, always drops the whole `customizedRequestData` --
                // this is what clears the MPID identity fields (first/last name, DOB) when the user
                // switches away from the identity-search mode (PBI 107807 -- stale identity leak)
                customizedRequestData: {},
                // entering the MPID advanced-search flow is mutually exclusive with the Name & DOB
                // search mode (PBI 107807 fix-wave F) -- both would otherwise route to
                // `searchByAdvancefilter()`/`defaultSearch()` unpredictably depending on which
                // toggle fired last, so turning advanced search on always turns this one off
                isIdentitySearchActive: false,
                isFetching: this.props.minSearchLength <= searchFilter.value.length,
                fetchingType: 'sortAndFilter',
                pagination: {
                    ...this.state.pagination,
                    startIndex: 0, // reset start index
                },
                searchableColumns: _searchableColumns
            }, () => {
                if( this.props.minSearchLength <= searchFilter.value.length ) {
                    this.fetchTableItems( { url: get( this.data, 'sortFilterConfig.url', {} ) } );
                } else {
                    return null;
                }
            }  ) );
        }

        if( 'sort' === type ) {

            // unset selected sort state
            const currentSelectedSortColumn = findKey( _sortAndFilter, val => includes( [ SORT_DIRECTIONS.ASCENDING, SORT_DIRECTIONS.DESCENDING ], val )  );
            if( currentSelectedSortColumn ) {
                _sortAndFilter[ currentSelectedSortColumn ] = null;
            }

            // update new sort state
            _sortAndFilter[ name ] = value;

        } else if( 'filter' === type ) {
            
            // update new filter state
            _sortAndFilter[ name ] = value;
        }

        // update new sort and filter state
        if( 'offline' === this.props.sortFilterMode ) {
            this.setState( {
                sortAndFilter: _sortAndFilter,
                pagination: {
                    ...this.state.pagination,
                    startIndex: 0, // reset start index
                }
            }, () => {
                this.sortAndFilterOffline();
            } );
        } else {
            this.setState( {
                isFetching: true,
                fetchingType: 'sortAndFilter',
                sortAndFilter: _sortAndFilter,
                pagination: {
                    ...this.state.pagination,
                    startIndex: 0, // reset start index
                }
            }, () => {
                this.fetchTableItems( { url: get( this.data, 'sortFilterConfig.url', {} ) } );
            } );
        }
    }

    /**
     * @desc Handle sort/filter offline.
     */
    sortAndFilterOffline() {
        log.debug( 'TableSearchWidget.sortAndFilterOffline()' );

        this.setState( {
            items: sortFilterOffline( {
                items: this.state.items,
                filters: this.state.sortAndFilter,
                columns: this.state.columns,
                originalItems: get( this.props.tableResponse, 'data.state.items', [] ),
            } ),
        } );
    }

    /**
     * @desc Handle pagination
     */
    handlePagination() {
        log.debug( 'TableSearchWidget.handlePagination()' );

        // if request is already in progress or pagination disabled, ignore pagination
        if( this.state.isFetching || this.state.disablePagination || ( this.state.isLiveMode && this.state.liveListError ) ) {
            return;
        }

        // increment start index
        const nextStartIndex = toInteger( get( this.state, 'pagination.startIndex', 0 ) ) + toInteger( get( this.state, 'pagination.chunkSize', 0 ) );
        
        // update pagination start index (startIndex)
        this.setState( {
            isFetching: true,
            fetchingType: 'pagination',
            pagination: {
                ...this.state.pagination,
                startIndex: nextStartIndex, // update start index
            }
        }, () => {
            this.fetchTableItems( { url: get( this.data, 'paginationConfig.url', {} ), paginate: true } );
        } );
    }

    /**
     * @desc Map `customizedRequestData` keys through the known identity field name
     * aliases (`IDENTITY_SEARCH_REQUEST_FIELD_NAMES`) so the MPID identity fields
     * (first/last name, DOB) are sent to the backend under the request field names
     * it expects. Keys with no alias (e.g. `status` from the card filter) pass through unchanged.
     *
     * Also normalizes the `dateOfBirth` value from the calendar widget's on-screen
     * `MM-DD-YYYY` display format to the ISO `yyyy-MM-dd` format the backend's
     * `LocalDate.parse(...)` requires. Only the outgoing request value is converted --
     * component state (and therefore the calendar's displayed value) is left untouched.
     */
    mapCustomizedRequestData( customizedRequestData ) {
        const _withNormalizedDateOfBirth = mapValues( customizedRequestData, ( value, key ) => {

            if( 'dateOfBirth' !== key || isEmpty( value ) ) {
                return value;
            }

            const _parsedDateOfBirth = moment( value, IDENTITY_DATE_OF_BIRTH_DISPLAY_FORMAT );

            // leave the value as-is if it doesn't parse as a valid `MM-DD-YYYY` date,
            // rather than sending "Invalid date" to the backend
            return _parsedDateOfBirth.isValid() ? _parsedDateOfBirth.format( IDENTITY_DATE_OF_BIRTH_REQUEST_FORMAT ) : value;
        } );

        return mapKeys( _withNormalizedDateOfBirth, ( value, key ) => get( IDENTITY_SEARCH_REQUEST_FIELD_NAMES, key, key ) );
    }

    getTableRequestData( pagination = this.state.pagination ) {
        return merge( {
            data: {
                ...fromPairs( map( this.state.filters, _filter => [ _filter.payloadKey, _filter.value ] ) ),
                ...this.state.sortAndFilter,
                ...pagination,
                ...this.state.selectedTab,
                ...this.mapCustomizedRequestData( this.state.customizedRequestData )
            }
        }, this.props.requestPayload );
    }

    beginTableRequest( { clearLiveListLoading = true } = {} ) {
        const requestSequence = ++this.tableRequestSequence;
        this.tableSearchWidgetService.cancel();
        this.liveListRequestInProgress = false;

        if( clearLiveListLoading && this.state.isFetchingLiveList ) {
            this.setState( {
                isFetching: false,
                isFetchingLiveList: false,
                fetchingType: null
            } );
        }

        return requestSequence;
    }

    mapLiveListItems( items ) {
        return this.props.liveListItemMapper ? this.props.liveListItemMapper( items ) : items;
    }

    notifyLiveListSelectionCleared( hadSelectedRows ) {
        if( hadSelectedRows && ! isNil( this.props.onSelectionCallBack ) ) {
            this.props.onSelectionCallBack( { values: [] } );
        }
    }

    async fetchCurrentList() {
        if( ! this.props.liveListEnabled || ! this.props.liveListUrl || this.state.isFetching || this.liveListRequestInProgress ) {
            return;
        }

        const pagination = { ...this.state.pagination, startIndex: 0 };
        const requestSequence = this.beginTableRequest();
        this.liveListRequestInProgress = true;
        const requestData = this.getTableRequestData( pagination );
        this.lastFailedLiveListRequest = null;
        this.setState( {
            isFetching: true,
            fetchingType: 'liveList',
            isFetchingLiveList: true,
            liveListError: false, liveListErrorMessage: null, liveListErrorReason: null
        } );

        try {
            const { status, items = [], paginationCapable = true, totalRecordCount, sourceAsOf, failureMessage, failureReason } = await this.tableSearchWidgetService.fetchItems( {
                url: this.props.liveListUrl,
                data: requestData
            } );

            if( requestSequence !== this.tableRequestSequence ) {
                return;
            }

            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                const _items = this.mapLiveListItems( items );
                const hadSelectedRows = ! isEmpty( this.state.selectedRows );
                this.lastFailedLiveListRequest = null;
                this.setState( {
                    isInitialData: false,
                    isFetching: false,
                    fetchingType: null,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    isLiveMode: true,
                    isFetchingLiveList: false,
                    liveListAsOf: sourceAsOf,
                    liveListError: false, liveListErrorMessage: null, liveListErrorReason: null,
                    selectedRows: [],
                    pagination,
                    totalRecordCount,
                    items: _items,
                    disablePagination: isNil( totalRecordCount )
                        ? _items.length < get( pagination, 'chunkSize', 0 ) || ! paginationCapable
                        : ! paginationCapable || toInteger( get( pagination, 'startIndex', 0 ) ) + _items.length >= toInteger( totalRecordCount )
                }, () => {
                    if( hadSelectedRows ) {
                        this.notifyLiveListSelectionCleared( hadSelectedRows );
                    }
                } );
            } else if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
                this.setState( { isFetching: false, fetchingType: null, isFetchingLiveList: false } );
            } else {
                this.lastFailedLiveListRequest = { url: this.props.liveListUrl, requestData, paginate: false, pagination };
                this.setState( {
                    isFetching: false,
                    fetchingType: null,
                    isFetchingLiveList: false,
                    liveListError: true,
                    liveListErrorMessage: failureMessage || null,
                    liveListErrorReason: failureReason || null
                } );
            }
        } catch( error ) {
            if( requestSequence === this.tableRequestSequence ) {
                this.lastFailedLiveListRequest = { url: this.props.liveListUrl, requestData, paginate: false, pagination };
                this.setState( {
                    isFetching: false,
                    fetchingType: null,
                    isFetchingLiveList: false,
                    liveListError: true
                } );
            }
        } finally {
            this.liveListRequestInProgress = false;
        }
    }

    handleCurrentListClick() {
        if( this.lastFailedLiveListRequest ) {
            return this.retryLiveListRequest();
        }

        return this.fetchCurrentList();
    }

    retryLiveListRequest() {
        if( ! this.lastFailedLiveListRequest || this.state.isFetching ) {
            return;
        }

        const { url, requestData, paginate, pagination } = this.lastFailedLiveListRequest;
        this.setState( { isFetching: true, fetchingType: 'liveList', isFetchingLiveList: true } );
        this.fetchTableItems( { url, requestData, paginate, pagination, retryLiveList: true } );
    }

    /**
     * @desc Fetch new table items based on search, filter and sort criteria.
     */
    async fetchTableItems( { url, paginate = false, requestData, pagination, retryLiveList = false } = {} ) {
        log.debug( 'TableSearchWidget.fetchTableItems()' );

        const requestSequence = this.beginTableRequest( { clearLiveListLoading: false } );
        const effectiveUrl = this.state.isLiveMode ? this.props.liveListUrl : url;
        const effectiveRequestData = requestData || this.getTableRequestData();
        const responsePagination = pagination || this.state.pagination;
        const isLiveResponse = this.state.isLiveMode || effectiveUrl === this.props.liveListUrl;

        if( ! retryLiveList ) {
            this.lastFailedLiveListRequest = null;
            this.setState( { liveListError: false, liveListErrorMessage: null, liveListErrorReason: null, isFetchingLiveList: false } );
        }

        // fetch table items from the service
        const { status, items, liveFallback = false, liveMemberLookup = false, paginationCapable = true, totalRecordCount, sourceAsOf, failureMessage, failureReason } = await this.tableSearchWidgetService.fetchItems( {
            url: effectiveUrl,
            data: effectiveRequestData
        } );

        if( requestSequence !== this.tableRequestSequence ) {
            return null;
        }

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
            this.setState( { isFetching: false, fetchingType: null, isFetchingLiveList: false } );
            return null; // ignore on request cancellations
        } else if( SERVICE_RESPONSE_STATUS.FAILURE === status ) {
            if( isLiveResponse ) {
                this.lastFailedLiveListRequest = { url: effectiveUrl, requestData: effectiveRequestData, paginate, pagination: responsePagination };
                this.setState( {
                    isFetching: false,
                    fetchingType: null,
                    isFetchingLiveList: false,
                    liveListError: true,
                    liveListErrorMessage: failureMessage || null,
                    liveListErrorReason: failureReason || null,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    fetchingType: null,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                }, () => failureMessage
                    ? errorOverlay.open( { message: failureMessage, size: SIZES.SMALL } )
                    : errorOverlay.open() );
            }
        } else {

            const _liveItems = markMemberSearchProvenance( isLiveResponse ? this.mapLiveListItems( items ) : items,
                this.props.showMemberSearchLiveBadge, liveMemberLookup, liveFallback );
            const hadSelectedRows = isLiveResponse && ! isEmpty( this.state.selectedRows );
            if( isLiveResponse ) {
                this.lastFailedLiveListRequest = null;
            }

            // append items based on pagination
            let _items = _liveItems;
            if( paginate ) {
                _items = this.state.items.concat( _liveItems );
            }

            // update state
            this.setState( {
                isInitialData: false,
                isFetching: false,
                fetchingType: null,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                isLiveMode: isLiveResponse,
                liveListError: isLiveResponse ? false : this.state.liveListError,
                liveListErrorMessage: isLiveResponse ? null : this.state.liveListErrorMessage,
                liveListErrorReason: isLiveResponse ? null : this.state.liveListErrorReason,
                liveListAsOf: isLiveResponse && sourceAsOf ? sourceAsOf : this.state.liveListAsOf,
                isFetchingLiveList: false,
                selectedRows: isLiveResponse ? [] : this.state.selectedRows,
                pagination: responsePagination,
                totalRecordCount,
                disablePagination: isNil( totalRecordCount )
                    ? _liveItems.length < get( responsePagination, 'chunkSize' ) || ! paginationCapable
                    : ! paginationCapable || toInteger( get( responsePagination, 'startIndex', 0 ) ) + _liveItems.length >= toInteger( totalRecordCount ),
                items: _items,
                liveFallbackKeys: [
                    ...( paginate ? this.state.liveFallbackKeys : [] ),
                    ...( true === liveFallback ? _liveItems.map( getMemberSearchSelectionKey ) : [] )
                ],
            }, () => {
                if( hadSelectedRows ) {
                    this.notifyLiveListSelectionCleared( hadSelectedRows );
                }
            } );
        }
    }

    /**
     * @desc Check if data view (table grid) is visible or hidden.
     */
    isDataViewVisible() {
        log.debug( 'TableSearchWidget.handleDataVisibilityChange()' );
        
        if( 'offline' === get( this.props, 'searchFilter', undefined ) ) {
            return true;
        }

        // return true for non-default templates
        if( this.props.templateType !== TABLE_TEMPLATE_TYPE.DEFAULT && this.props.templateType !== TABLE_TEMPLATE_TYPE.ADVANCED_SEARCH ) {
            return true;
        }

        // return `true` if `searchQuery` length is `GTE` minimum length
        const searchFilter = find( this.state.filters, { name: 'search' } );
        if( ! isEmpty( searchFilter ) ) {
            return size( searchFilter.value ) >= this.props.minSearchLength;
        }

        // return `false` otherwise
        return false;
    }

    /**
     * @desc executed when user switched the minimal tab.
     */
    handleTabChange( { tab } ) {
        log.debug( 'TableSearchWidget.handleTabChange()' );

        const sortAndFilter = get( this.props, 'tableResponse.data.state.sortAndFilter', {} );

        const payloadKey = get( this.data, 'tabsConfig.payloadKey', undefined );

        this.setState( {
            sortAndFilter,
            isFetching: true,
            selectedTab: {
                [ payloadKey ]: tab.value
            }
        }, () => {
            this.fetchTableItems( { url: get( this.data, 'filtersConfig.url', {} ) } );
        } );
    }

    /**
     * @desc executed when user wants to refresh the row
     */
    refreshTable( { id, requestPayloadForRefresh, rowData = {} } = {} ) {
        log.debug( 'TableSearchWidget.refreshTable()' );

        if( ! isEmpty( rowData ) ) {

            const items = cloneDeep( this.state.items );

            // get the index of the row
            const tableItemIndex = findIndex( items, { id: rowData.id } );

            if( -1 !== tableItemIndex ) {
                items[ tableItemIndex ] = rowData;
                this.setState( { items } );
            }

        } else {

            // cancel ongoing request
            this.beginTableRequest();

            // set item is fetching to true
            const _items = cloneDeep( this.state.items );

            forEach( _items, _item => {
                if( id === _item.id ) {
                    _item.fetching = true;
                }
            } );

            this.setState( { items: _items }, async() => {

                // fetch table items from the service
                const { status, data } =  await this.tableSearchWidgetService.refreshItem( {
                    url: get( this.data, 'refreshRowConfig.url', {} ),
                    data: requestPayloadForRefresh,
                } );

                // check if response status was success
                if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
                    
                    return null; // ignore on request cancellations
                } else if( SERVICE_RESPONSE_STATUS.FAILURE === status ) {
                    this.setState( {
                        isFetching: false,
                        networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                    }, () => {
                        errorOverlay.open();
                    } );
                } else {

                    // updating the values of the item and also setting isFetching false
                    const _data = cloneDeep( this.state.items );

                    forEach( _data, row => {
                        if( head( data ).id === row.id ) {
                            const info = head( data );
                            row.values = get( info, 'values', {} );
                            row.actionsVisibility =  get( info, 'actionsVisibility', {} );
                            row.iconsVisibility = get( info, 'iconsVisibility', {} );
                            row.fetching = false;
                        }
                    } );
            
                    // update state
                    this.setState( {
                        isInitialData: false,
                        isFetching: false,
                        networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                        items: _data
                    } );
                }
            } );
        }
        
    }

    /**
     * @desc this function is called to remove the row from a table
     */
    removeRowTable( { id } ) {
        log.debug( 'TableSearchWidget.removeRowTable()' );

        const items = cloneDeep( this.state.items );

        // filter the list
        const newList = filter( items, item => {
            return item.id !== id;
        } );

        this.setState( { items: newList } );
    }

    /**
     * @desc executed when user clicked on an action
     */
    async handleActionClick( { type, value, item, parentItem = {}, selection = [] } ) {
        log.debug( 'TableSearchWidget.handleActionClick()' );
        const memberSource = this.props.showMemberSearchLiveBadge ? { liveMemberLookup: true === get( item, '__liveMemberSearch', false ) } : {};
        const liveFallback = typeof get( item, '__memberSearchLiveFallback' ) === 'boolean'
            ? item.__memberSearchLiveFallback : this.state.liveFallbackKeys.includes( getMemberSearchSelectionKey( item ) );
        if( 'remove' === get( value, 'name', '' ) ) {
            this.props.onActionClick( { type, value, item, parentItem, selection, ...memberSource, liveFallback }, this.removeRowTable );
        } else if( ! get( value, 'config.refreshRow', false ) && ! this.props.enabledCallBack ) {
            this.props.onActionClick( { type, value, item, parentItem, selection, ...memberSource, liveFallback } );
        } else {
            this.props.onActionClick( { type, value, item, parentItem, selection, ...memberSource, liveFallback }, this.refreshTable );
        }

    }
 
    // when component will receive new props, update items
    UNSAFE_componentWillReceiveProps( nextProps ) {
        log.debug( 'TableSearchWidget.componentWillReceiveProps()' );

        if ( this.props.persistTableData ) {

            return;
            
        } else if( this.props.updateTableOnChange && (
            ! isEqual( get( nextProps, 'tableResponse.data.state.items', [] ), this.state.items ) ||
            get( nextProps, 'tableResponse.data.state.liveFallback', false ) !== get( this.props, 'tableResponse.data.state.liveFallback', false ) ||
            ( nextProps.showMemberSearchLiveBadge && get( nextProps, 'tableResponse.data.state.liveMemberLookup', false ) !== get( this.props, 'tableResponse.data.state.liveMemberLookup', false ) )
        ) ) {
            this.setState( {
                items: markMemberSearchProvenance( get( nextProps, 'tableResponse.data.state.items', [] ),
                    nextProps.showMemberSearchLiveBadge, get( nextProps, 'tableResponse.data.state.liveMemberLookup', false ),
                    get( nextProps, 'tableResponse.data.state.liveFallback', false ) ),
                liveFallbackKeys: true === get( nextProps, 'tableResponse.data.state.liveFallback', false )
                    ? get( nextProps, 'tableResponse.data.state.items', [] ).map( getMemberSearchSelectionKey ) : [],
            } );
        } else {
            const { filters } = getVisibleTableEntities( this.props.tableResponse );

            this.setState( {  filters } );
        }
    }

    async handleOnlineSearch( { config, search } ) {
        log.debug( 'TableSearchWidget.handleOnlineSearch()' );

        const requestSequence = this.beginTableRequest();
        // fetch table items from the service
        const { status, options } =  await this.tableSearchWidgetService.fetchFilterSearchOptions( {
            url: get( config, 'url', {} ),
            data: {
                data: { [ `${config.searchQuery}` ]: search.query }
            },
        } );

        if( requestSequence !== this.tableRequestSequence ) {
            return null;
        }

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
            
            return null; // ignore on request cancellations

        } else if( SERVICE_RESPONSE_STATUS.FAILURE === status ) {

            errorOverlay.open();

            return { options: [], status };

        } else {

            return { options: options, status };
        }
    }

    /*********************************************/

    /**
     * @desc Members without indexed coverage arrive with `initialListSource: 'live'`: load the
     * live list once on mount instead of showing the (empty) indexed list. Later state changes
     * never re-trigger this, so a failed load is retried only via the retry button.
     */
    componentDidMount() {
        if( INITIAL_LIST_SOURCE_LIVE === get( this.props, 'tableResponse.data.state.initialListSource' ) ) {
            this.fetchCurrentList();
        }
    }

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.tableRequestSequence += 1;
        this.tableSearchWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {

        // get table search widget view
        const TableSearchWidgetView = TABLE_VIEW_TEMPLATES[ this.props.templateType ];

        return (
            <>
                
            {this.props.flow === 'prescriptions' && ! this.state.isLiveMode && <details className='export-menu'>
                <summary className='export-menu__trigger'>Export</summary>
                <div className='export-menu__options' onClick={ closeExportMenu }>
                <Button variant="contained" onClick={async () => {
                    const requestBody = {...this.props.requestPayload};
                    const _filters = cloneDeep( this.state.filters );
                    const _filter = find( _filters, { name: "search" } );
                    if (_filter.value) {
                        const response = await fetch(this.props.componentData?.elements?.urls?.memberPrescriptionSearchExcelDownload, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(merge( {
                                data: {
                                    searchQuery: _filter['value'], // global filters values
                                    ...this.state.sortAndFilter, // column sort and filter values
                                    ...this.state.pagination, // pagination
                                    ...this.state.selectedTab,
                                    ...this.state.customizedRequestData
                                }
                            }, this.props.requestPayload ),)
                        });
                        
                        // Extract the Content-Disposition header
                        const contentDisposition = response.headers.get('Content-Disposition');

                        if (!contentDisposition) {
                            throw new Error('Content-Disposition header not found');
                        }
                        // Extract the filename from the Content-Disposition header
                        const filenameMatch = contentDisposition.match(/filename=(.*)/)[1];
                        const filename = filenameMatch ? filenameMatch : 'downloaded-file.xls';

                        console.log(contentDisposition, filenameMatch);

                        // Convert the response to a Blob
                        const blob = await response.blob();
                        const blobUrl = window.URL.createObjectURL(blob);

                        // Create a link element and trigger the download
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = filename; // Use the extracted filename
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                    } else {
                        const response = await fetch(this.props.componentData?.elements?.urls?.memberPrescriptionExcelDownload, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(requestBody)
                        });
                        
                        // Extract the Content-Disposition header
                        const contentDisposition = response.headers.get('Content-Disposition');

                        if (!contentDisposition) {
                            throw new Error('Content-Disposition header not found');
                        }


                        // Extract the filename from the Content-Disposition header
                        const filenameMatch = contentDisposition.match(/filename=(.*)/)[1];
                        const filename = filenameMatch ? filenameMatch : 'downloaded-file.xls';

                        console.log(contentDisposition, filenameMatch);

                        // Convert the response to a Blob
                        const blob = await response.blob();
                        const blobUrl = window.URL.createObjectURL(blob);

                        // Create a link element and trigger the download
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = filename; // Use the extracted filename
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                    }
                    }
                    }
                >
                    Export to excel
                </Button>
                <Button variant="contained" onClick={async () => {
                    const requestBody = {...this.props.requestPayload};
                    const _filters = cloneDeep( this.state.filters );
                    const _filter = find( _filters, { name: "search" } );
                    if (_filter.value) {
                        const responseData = await fetch(this.props.componentData?.elements?.urls?.memberPrescriptionSearchPdfDownload, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(merge( {
                                data: {
                                    searchQuery: _filter['value'], // global filters values
                                    ...this.state.sortAndFilter, // column sort and filter values
                                    ...this.state.pagination, // pagination
                                    ...this.state.selectedTab,
                                    ...this.state.customizedRequestData
                                }
                            }, this.props.requestPayload ),)
                            
                        })
                        // Extract the Content-Disposition header
                        const contentDisposition = responseData.headers.get('Content-Disposition');

                        if (!contentDisposition) {
                            throw new Error('Content-Disposition header not found');
                        }

                        // Extract the filename from the Content-Disposition header
                        const filenameMatch = contentDisposition.match(/filename=(.*)/)[1];
                        const filename =  filenameMatch ? filenameMatch : 'downloaded-file.pdf';

                        console.log(contentDisposition, filenameMatch);

                        // Convert the responseData to a Blob
                        const blob = await responseData.blob();
                        const blobUrl = window.URL.createObjectURL(blob);

                        // Create a link element and trigger the download
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = filename; // Use the extracted filename
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                    } else {
                        const responseData = await fetch(this.props.componentData?.elements?.urls?.memberPrescriptionPdfDownload, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(requestBody)
                        });
                        // Extract the Content-Disposition header
                        const contentDisposition = responseData.headers.get('Content-Disposition');

                        if (!contentDisposition) {
                            throw new Error('Content-Disposition header not found');
                        }

                        // Extract the filename from the Content-Disposition header
                        const filenameMatch = contentDisposition.match(/filename=(.*)/)[1];

                        console.log(contentDisposition, filenameMatch);
                        const filename =  filenameMatch ? filenameMatch : 'downloaded-file.pdf';

                        // Convert the response to a Blob
                        const blob = await responseData.blob();
                        const blobUrl = window.URL.createObjectURL(blob);

                        // Create a link element and trigger the download
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = filename; // Use the extracted filename
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                    }
                    }
                    }
                >
                    Export to PDF
                </Button>
                </div>
            </details>}
            
            <TableSearchWidgetView
                { ...this.state }
                visibility={ this.data.visibility }
                widgetConfig={ this.data.widgetConfig }
                searchableColumns={ this.state.searchableColumns }
                actionsConfig={ this.state.isLiveMode
                    ? { ...this.data.actionsConfig, selectionActions: {} }
                    : this.data.actionsConfig }
                className={ this.props.className }
                classNameTable = { this.props.classNameTable }
                layoutConfig={ this.props.layoutConfig }
                titleClassName={ this.props.titleClassName }
                onFilterChange={ this.handleFiltersChange }
                onSortAndFilter={ this.handleSortAndFilter }
                onPagination={ this.handlePagination }
                onActionClick={ this.handleActionClick }
                onDataVisibilityChange={ this.handleDataVisibilityChange }
                disableDataView={ ! this.isDataViewVisible() }
                onSelect={ this.handleSelection }
                selectedRows= {this.state.selectedRows}
                tableRowViewTemplate={ this.props.tableRowViewTemplate }
                tabsConfig={ this.data.tabsConfig }
                onTabChange={ this.handleTabChange }
                fetchFilterSearchOptions={ this.handleOnlineSearch }
                dynamicSelectedOptions={ this.state.dynamicSelectedOptions }
                liveListEnabled={ this.props.liveListEnabled && Boolean( this.props.liveListUrl ) }
                compactLiveListCopy={ this.props.compactLiveListCopy }
                onGetCurrentList={ this.handleCurrentListClick }
                description={ this.data.sortFilterConfig.description }
                values={ this.data.values }
                options={ this.data.options }
                mailOrderAccountStatusConfig= { this.data.mailOrderAccountStatusConfig }
                callState={ this.state.callState }
                identitySearchEnabled={ this.props.identitySearchEnabled }
            />
            </>
        );
    }
}

// set display name
TableSearchWidget.displayName = 'TableSearchWidget';

// set default props
TableSearchWidget.defaultProps = {
    requestPayload: {},
    tableResponse: {},
    layoutConfig: {},
    templateType: TABLE_TEMPLATE_TYPE.DEFAULT,
    minSearchLength: 3,
    onActionClick: noop,
    tableRowViewTemplate: ROW_VIEW_TEMPLATE_TYPE.DEFAULT,
    updateTableOnChange: false,
    disablePagination: false,
    sortFilterMode: null,
    onSelectionCallBack: null,
    enabledCallBack: false,
    liveListEnabled: false,
    liveListUrl: null,
    liveListItemMapper: null,
    compactLiveListCopy: false,
    identitySearchEnabled: false
};

// prop types
TableSearchWidget.propTypes = {};
